import axiosInstance from '../config/axios.js';
import { downloadBlob, filenameFromContentDisposition } from '../utils/downloadBlob.js';

const BASE = '/company-settlements';

let settlementJob = null;
let settlementJobOpen = false;
let settlementJobSettledAt = 0;
let pendingRecordedSettlement = null;
const inflightGets = new Map();
let summaryTail = Promise.resolve();

const coalesceGet = (key, runner) => {
  const existing = inflightGets.get(key);
  if (existing) return existing;
  const pending = Promise.resolve()
    .then(runner)
    .finally(() => {
      if (inflightGets.get(key) === pending) inflightGets.delete(key);
    });
  inflightGets.set(key, pending);
  return pending;
};

/** Comma-separated hub ids — Flask getlist + _parse_hub_ids both accept this. */
export const settlementHubQueryParams = (params = {}) => {
  const next = { ...params };
  const ids = next.location_hub_ids ?? next.hub_ids;
  delete next.location_hub_ids;
  delete next.hub_ids;
  if (Array.isArray(ids) && ids.length) {
    const joined = ids.map(String).filter(Boolean).join(',');
    if (joined) {
      next.location_hub_ids = joined;
      next.hub_ids = joined;
    }
  } else if (typeof ids === 'string' && ids.trim()) {
    next.location_hub_ids = ids.trim();
    next.hub_ids = ids.trim();
  }
  return next;
};

const companySettlementService = {
  getDueFlag: async () => {
    const response = await axiosInstance.get(`${BASE}/due-flag`);
    return response.data;
  },

  listCompanyOwes: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/company-owes`, {
      params: settlementHubQueryParams(params),
    });
    return response.data;
  },

  getSummary: async (params = {}) => {
    const query = settlementHubQueryParams(params);
    const key = `summary:${JSON.stringify(query)}`;
    const run = () => coalesceGet(key, async () => {
      const response = await axiosInstance.get(`${BASE}/summary`, { params: query });
      return response.data;
    });
    const job = summaryTail.then(run, run);
    summaryTail = job.catch(() => undefined);
    return job;
  },

  listBatches: async (params = {}) => {
    const query = settlementHubQueryParams(params);
    return coalesceGet(`batches:${JSON.stringify(query)}`, async () => {
      const response = await axiosInstance.get(`${BASE}/batches`, { params: query });
      return response.data;
    });
  },

  getBatch: async (batchId) => {
    const response = await axiosInstance.get(`${BASE}/batches/${encodeURIComponent(batchId)}`);
    return response.data;
  },

  submitBatch: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/batches`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  approveBatch: async (batchId, payload = {}) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/approve`,
      payload,
    );
    return response.data;
  },

  listBranchHandoffAgents: async () => {
    const response = await axiosInstance.get(`${BASE}/branch-handoff/agents`);
    return response.data;
  },

  settleToBranchAgent: async (payload) => {
    const response = await axiosInstance.post(`${BASE}/branch-handoff`, payload);
    return response.data;
  },

  listBranchAgents: async (locationHubId, { transferPreview = false } = {}) => {
    const response = await axiosInstance.get(`${BASE}/branch-agents`, {
      params: {
        location_hub_id: locationHubId,
        ...(transferPreview ? { transfer_preview: '1' } : {}),
      },
    });
    return response.data;
  },

  recordPreview: async (params) => {
    const response = await axiosInstance.get(`${BASE}/record-preview`, { params });
    return response.data;
  },

  recordReceived: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/record-received`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  payAgent: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/pay-agent`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  rejectBatch: async (batchId, payload = {}) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/reject`,
      payload,
    );
    return response.data;
  },

  editBatch: async (batchId, amount) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/edit`,
      { amount },
    );
    return response.data;
  },

  deleteBatch: async (batchId) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/delete`,
    );
    return response.data;
  },

  stageRecordedSettlement(row) {
    pendingRecordedSettlement = row;
    window.dispatchEvent(new CustomEvent('company-settlement-changed'));
  },

  pendingRecordedSettlement() {
    return pendingRecordedSettlement;
  },

  clearPendingSettlement(notify = true) {
    if (!pendingRecordedSettlement) return;
    pendingRecordedSettlement = null;
    if (notify) window.dispatchEvent(new CustomEvent('company-settlement-changed'));
  },

  isSettlementJobOpen() {
    return settlementJobOpen;
  },

  trackSettlementJob(promise) {
    settlementJob = promise;
    settlementJobOpen = true;
    promise.finally(() => {
      settlementJobOpen = false;
      settlementJobSettledAt = Date.now();
      window.dispatchEvent(new CustomEvent('company-settlement-changed'));
    });
    return promise;
  },

  currentSettlementJob() {
    return settlementJob;
  },

  settlementJobSettledRecently(ms = 20000) {
    return Boolean(settlementJobSettledAt) && Date.now() - settlementJobSettledAt < ms;
  },

  downloadBatchExcel: async (batchId) => {
    const response = await axiosInstance.get(
      `${BASE}/batches/${encodeURIComponent(batchId)}/excel`,
      { responseType: 'blob' },
    );
    const name = filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      `${batchId}-returned.xlsx`,
    );
    return downloadBlob(
      response.data,
      name,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },

  downloadExcel: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/statement.xlsx`, {
      params: settlementHubQueryParams(params),
      responseType: 'blob',
    });
    const name = filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'commission_settlement.xlsx',
    );
    return downloadBlob(
      response.data,
      name,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },

  downloadPdf: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/statement.pdf`, {
      params: settlementHubQueryParams(params),
      responseType: 'blob',
    });
    const name = filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'commission_settlement.pdf',
    );
    return downloadBlob(response.data, name, 'application/pdf');
  },
};

export default companySettlementService;
